/**
 * Packaging Utils — централизованная логика управления упаковкой
 * Tiefkühlverpackung (frozen) и Kühlverpackung (cooled)
 *
 * Правила:
 * - product_type === 'frozen' → добавить Tiefkühlverpackung (1 шт)
 * - product_type === 'cooled' → добавить Kühlverpackung (1 шт)
 * - product_type === 'fresh' или другое → ничего
 * - Только цифровые товары (requires_shipping=false) → ничего
 * - Обе упаковки могут быть одновременно
 *
 * АРХИТЕКТУРА (после рефакторинга на CartStore):
 * - Это модуль ЧИСТЫХ функций решения по упаковке (getPackagingDiff,
 *   getPackagingItemsForAdd) + конфиг.
 * - Источник корзины — window.CartStore (своего кэша больше нет).
 * - Реактивная коррекция (один /cart/update после мутации) выполняется
 *   ВНУТРИ CartStore (_reconcilePackaging). Старый ensurePackaging оставлен
 *   как тонкий шим-делегат, чтобы не падали возможные старые вызовы.
 */
window.PackagingUtils = (() => {
  /**
   * Читаем ID упаковок из скрытого input .additional-product в theme.liquid
   */
  function getConfig() {
    const el = document.querySelector('.additional-product');
    if (!el) return null;
    return {
      frozenId: el.dataset.product || '',
      cooledId: el.dataset.productCooled || '',
    };
  }

  // ─── Кэш корзины: делегируем в CartStore (единый источник правды) ──────────

  function getCachedCart() {
    return window.CartStore ? window.CartStore.getCart() : null;
  }

  async function fetchFullCart() {
    if (window.CartStore) {
      const current = window.CartStore.getCart();
      if (current) return current;
      return window.CartStore.refresh();
    }
    // Фолбэк, если стор не загружен (не должно происходить).
    const res = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  // Кэш теперь в CartStore — эти методы оставлены как no-op для совместимости.
  function invalidateCache() {}
  function updateCache() {}

  /**
   * Определение типа товара по product_type.
   * Shopify локализует product_type в storefront/cart JSON на /ru/... маршрутах,
   * поэтому перед сравнением нормализуем известные значения. Fresh/Frisch/Свежий
   * явно не считаем cooled, чтобы не добавлять упаковку свежим товарам.
   */
  function normalizeProductType(productType) {
    const type = (productType || '').toLowerCase().trim();
    if (!type) return '';
    if (type === 'fresh' || type.includes('frisch') || type.includes('свеж')) return 'fresh';
    if (type === 'frozen' || type.includes('заморож') || type.includes('gefror') || type.includes('tiefkühl') || type.includes('tiefkuhl')) return 'frozen';
    if (type === 'cooled' || type.includes('охлаж') || type.includes('gekühlt') || type.includes('gekuhlt')) return 'cooled';
    return type;
  }

  function isFrozenType(productType) {
    return normalizeProductType(productType) === 'frozen';
  }

  function isCooledType(productType) {
    return normalizeProductType(productType) === 'cooled';
  }

  function getVariantId(item) {
    return String(item.variant_id != null ? item.variant_id : item.id);
  }

  /**
   * Анализ корзины: какие упаковки нужны, какие уже есть
   * Возвращает { toAdd: string[], toRemove: string[], quantityFixes: {} }
   */
  function getPackagingDiff(cart, frozenId, cooledId) {
    const items = (cart && cart.items) || [];
    const fId = String(frozenId || '');
    const cId = String(cooledId || '');

    // Отделяем «обычные» товары от упаковки (id в cart.js — variant_id)
    const otherItems = items.filter(
      (it) => getVariantId(it) !== fId && getVariantId(it) !== cId
    );

    // Если корзина содержит только цифровые товары — упаковка не нужна
    const hasPhysicalItems = otherItems.some((it) => it.requires_shipping);
    if (otherItems.length > 0 && !hasPhysicalItems) {
      const toRemove = [];
      if (fId && items.some((it) => getVariantId(it) === fId)) toRemove.push(fId);
      if (cId && items.some((it) => getVariantId(it) === cId)) toRemove.push(cId);
      return { toAdd: [], toRemove, quantityFixes: {} };
    }

    const needsFrozen = otherItems.some((it) => isFrozenType(it.product_type));
    const needsCooled = otherItems.some((it) => isCooledType(it.product_type));

    const frozenExists = fId ? items.some((it) => getVariantId(it) === fId) : false;
    const cooledExists = cId ? items.some((it) => getVariantId(it) === cId) : false;

    const toAdd = [];
    const toRemove = [];

    // Frozen packaging
    if (fId) {
      if (needsFrozen && !frozenExists) toAdd.push(fId);
      if (!needsFrozen && frozenExists) toRemove.push(fId);
    }

    // Cooled packaging
    if (cId) {
      if (needsCooled && !cooledExists) toAdd.push(cId);
      if (!needsCooled && cooledExists) toRemove.push(cId);
    }

    // Enforce quantity = 1
    const frozenItem = fId ? items.find((it) => getVariantId(it) === fId) : null;
    const cooledItem = cId ? items.find((it) => getVariantId(it) === cId) : null;
    const quantityFixes = {};
    if (frozenItem && frozenItem.quantity > 1) quantityFixes[fId] = 1;
    if (cooledItem && cooledItem.quantity > 1) quantityFixes[cId] = 1;

    return { toAdd, toRemove, quantityFixes };
  }

  /**
   * Определить, какие упаковки нужно добавить в items[] запроса при добавлении товара
   * (проактивное добавление — в тот же запрос)
   */
  function getPackagingItemsForAdd(cart, frozenId, cooledId, newProductType, newProductRequiresShipping = true) {
    const items = cart ? (cart.items || []) : [];

    // Если добавляемый товар — цифровой, и в корзине нет физических
    if (!newProductRequiresShipping) {
      const hasPhysical = items.some(
        (it) => it.requires_shipping && String(it.id) !== String(frozenId) && String(it.id) !== String(cooledId)
      );
      if (!hasPhysical) return [];
    }

    // Упаковки уже в корзине?
    const frozenExists = frozenId ? items.some((it) => getVariantId(it) === String(frozenId)) : false;
    const cooledExists = cooledId ? items.some((it) => getVariantId(it) === String(cooledId)) : false;

    // Есть ли уже frozen/cooled товары в корзине (кроме упаковок)?
    const otherItems = items.filter(
      (it) => getVariantId(it) !== String(frozenId) && getVariantId(it) !== String(cooledId)
    );
    const hasFrozenInCart = otherItems.some((it) => isFrozenType(it.product_type));
    const hasCooledInCart = otherItems.some((it) => isCooledType(it.product_type));

    const isAddingFrozen = isFrozenType(newProductType);
    const isAddingCooled = isCooledType(newProductType);

    const result = [];

    if (frozenId && !frozenExists && (isAddingFrozen || hasFrozenInCart)) {
      result.push(frozenId);
    }
    if (cooledId && !cooledExists && (isAddingCooled || hasCooledInCart)) {
      result.push(cooledId);
    }

    return result;
  }

  /**
   * ensurePackaging — ШИМ совместимости.
   * Реактивная коррекция теперь выполняется внутри CartStore автоматически
   * после каждой мутации. Старые вызовы из cart.js/cart-drawer.js не обязаны
   * ничего делать; для гарантии актуальности дергаем refresh (он сам, при
   * необходимости, не нужен — стор уже свежий). onComplete вызывается с null.
   */
  function ensurePackaging(cartData, sections, onComplete) {
    if (typeof onComplete === 'function') {
      // Коррекция уже применена стором до публикации cart:changed — доп. рендер не нужен.
      try {
        onComplete(null);
      } catch (e) {}
    }
  }

  return {
    getConfig,
    fetchFullCart,
    getCachedCart,
    invalidateCache,
    updateCache,
    isFrozenType,
    isCooledType,
    getPackagingDiff,
    getPackagingItemsForAdd,
    ensurePackaging,
  };
})();
